import { c as _c } from "react/compiler-runtime";
import { useId } from 'react';
import { styled } from '@classify/react-core-components/styles';
import Typography from '@classify/react-core-components/Typography';
import LinearProgress from '@classify/react-core-components/LinearProgress';
import InfoIcon from '@mui/icons-material/Info';
const defaultTranslations = {
  label: 'Your profile is complete at'
};
type Variant = 'inline' | 'block' | 'responsive';
export interface Props {
  completion: number;
  threshold?: number;
  variant?: Variant;
  hasInfoIcon?: boolean;
  translations?: {
    label: string;
  };
}
const Root = styled('div', {
  shouldForwardProp: prop => !['variant'].includes(prop as string)
})<{
  variant: Variant;
}>(({
  theme: {
    spacing,
    breakpoints
  },
  variant
}) => {
  const styles = {
    block: {
      gap: spacing(1),
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    inline: {
      gap: spacing(1),
      flexDirection: 'row',
      alignItems: 'center'
    },
    responsive: {
      flexDirection: 'column',
      gap: spacing(1),
      width: '100%',
      [breakpoints.up('sm')]: {
        alignItems: 'center',
        flexDirection: 'row'
      }
    }
  } as const;
  return {
    display: 'flex',
    flexGrow: 1,
    ...styles[variant]
  };
});
const TextContainer = styled('div')(({
  theme: {
    spacing
  }
}) => ({
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacing(0.5)
}));
const ProgressBar = styled(LinearProgress, {
  shouldForwardProp: prop => !['hasError'].includes(prop as string)
})<{
  hasError?: boolean;
}>(({
  hasError,
  theme: {
    palette
  }
}) => ({
  flexGrow: 1,
  width: '100%',
  '.MuiLinearProgress-bar': hasError ? {
    backgroundColor: palette.form.label.error
  } : undefined
}));
export const getCompletionPercentage = (completion: number) => `${Math.round(completion * 100)}%`;
const ProfileCompletion = t0 => {
  const $ = _c(24);
  const {
    completion,
    translations: t1,
    threshold: t2,
    variant: t3,
    hasInfoIcon
  } = t0;
  const translations = t1 === undefined ? defaultTranslations : t1;
  const threshold = t2 === undefined ? 0.85 : t2;
  const variant = t3 === undefined ? "responsive" : t3;
  let t4;
  if ($[0] !== completion) {
    t4 = getCompletionPercentage(completion);
    $[0] = completion;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const displayCompletion = t4;
  const isBellowThreshold = Boolean(threshold && completion < threshold);
  const progressColor = isBellowThreshold ? "form.label.error" : "brand.main";
  const labelId = useId();
  let t5;
  if ($[2] !== hasInfoIcon) {
    t5 = hasInfoIcon && <InfoIcon sx={{
      color: "info.main"
    }} />;
    $[2] = hasInfoIcon;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== translations.label) {
    t6 = translations.label && <Typography component="span" color="text.onMain">{translations.label}{" "}</Typography>;
    $[4] = translations.label;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== progressColor) {
    t7 = {
      color: progressColor
    };
    $[6] = progressColor;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== displayCompletion || $[9] !== t7) {
    t8 = <Typography component="span" variant="h5" sx={t7}>{displayCompletion}</Typography>;
    $[8] = displayCompletion;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== labelId || $[12] !== t5 || $[13] !== t6 || $[14] !== t8) {
    t9 = <TextContainer id={labelId}>{t5}{t6}{t8}</TextContainer>;
    $[11] = labelId;
    $[12] = t5;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== completion || $[17] !== isBellowThreshold || $[18] !== labelId) {
    t10 = <ProgressBar value={completion} maxValue={1} size="large" shape="rounded" hasError={isBellowThreshold} aria-labelledby={labelId} />;
    $[16] = completion;
    $[17] = isBellowThreshold;
    $[18] = labelId;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9 || $[22] !== variant) {
    t11 = <Root variant={variant}>{t9}{t10}</Root>;
    $[20] = t10;
    $[21] = t9;
    $[22] = variant;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
};
export default ProfileCompletion;